import type { ApiError, ApiFieldError } from '@talentbabu/types';
import { isAxiosError } from 'axios';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

export function apiErrorOf(err: unknown): ApiError | undefined {
  if (isAxiosError<ApiError>(err) && err.response?.data && err.response.data.success === false)
    return err.response.data;
  return undefined;
}

export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const api = apiErrorOf(err);
  if (api) return api.message;
  if (isAxiosError(err) && !err.response) return 'Cannot reach the server. Check your connection and try again.';
  return fallback;
}

export function errorCode(err: unknown): string | undefined {
  return apiErrorOf(err)?.errors[0]?.code;
}

/**
 * Maps server field errors onto react-hook-form fields.
 * Returns the errors that did not match a form field (to show as a banner).
 */
export function applyFieldErrors<T extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<T>,
  fields: readonly string[],
): ApiFieldError[] {
  const api = apiErrorOf(err);
  if (!api) return [];
  const unmatched: ApiFieldError[] = [];
  for (const e of api.errors) {
    if (e.field && fields.some((f) => e.field === f || e.field?.startsWith(`${f}.`))) {
      setError(e.field as Path<T>, { type: 'server', message: e.message });
    } else {
      unmatched.push(e);
    }
  }
  return unmatched;
}
